import type { QueueState } from './useWriteQueue';

interface Props {
  state: QueueState;
  onRetry: () => void;
  onClose: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  syncing: 'Syncing',
  failed_retry: 'Backoff',
  failed_terminal: 'Failed',
};

export function RecoveryPanel({ state, onRetry, onClose }: Props) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-30 flex items-center justify-center bg-graphite-900/40 px-4"
    >
      <div className="w-full max-w-lg rounded-lg border border-graphite-100 bg-surface p-6 shadow-lg">
        <div className="flex items-baseline justify-between">
          <h2 className="font-sans text-lg font-semibold text-graphite-900">Unsynced writes</h2>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-xs text-graphite-500 hover:text-graphite-900"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="mt-1 font-sans text-sm text-graphite-700">
          Audit rows are saved. Zoho dispatch is pending or failed. Retrying re-arms every row for
          an immediate drain.
        </p>
        <dl className="mt-4 grid grid-cols-3 gap-4 font-mono text-xs">
          <Stat label="pending" value={state.pending} />
          <Stat label="backoff" value={state.failedRetry} accent={state.failedRetry > 0} />
          <Stat label="failed" value={state.failedTerminal} accent={state.failedTerminal > 0} />
        </dl>

        {state.rows.length > 0 ? (
          <ul className="mt-4 max-h-64 divide-y divide-graphite-100 overflow-y-auto rounded-md border border-graphite-100 bg-surface-sunk">
            {state.rows.map((row) => (
              <li
                key={row.id}
                className="flex items-baseline justify-between gap-3 px-3 py-2 font-mono text-xs"
              >
                <span className="text-graphite-900">{row.kind}</span>
                <span className="text-graphite-500">
                  {STATUS_LABELS[row.status] ?? row.status} · {row.attempts}×
                  {row.last_error ? ` · ${row.last_error}` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 rounded-md border border-dashed border-graphite-300 bg-surface-sunk px-3 py-4 text-center font-mono text-xs text-graphite-500">
            Nothing to recover. Queue is empty.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 font-sans text-sm text-graphite-700 hover:bg-surface-sunk"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onRetry}
            disabled={state.failedRetry + state.failedTerminal === 0}
            className="rounded-md bg-graphite-900 px-3 py-2 font-sans text-sm font-medium text-surface hover:bg-graphite-700 disabled:opacity-40"
          >
            Retry all
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div>
      <dt className="text-graphite-500">{label}</dt>
      <dd className={`mt-1 text-lg ${accent ? 'text-signal-amber' : 'text-graphite-900'}`}>
        {value}
      </dd>
    </div>
  );
}
